import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams } from 'react-router-dom'
import { Mail, AlertCircle, CheckCircle } from 'lucide-react'

export default function LoginPage() {
  const { loginWithRedirect } = useAuth0()
  const [searchParams] = useSearchParams()

  // Check for Auth0 error in URL (e.g., email not verified)
  const error = searchParams.get('error')
  const errorDescription = searchParams.get('error_description')

  // Check if user just registered via the new registration flow
  const registered = searchParams.get('registered')
  const isTrialRegistration = registered === 'trial'

  const isEmailVerificationError = error === 'access_denied' &&
    (errorDescription?.toLowerCase().includes('email') ||
     errorDescription?.toLowerCase().includes('weryfikować') ||
     errorDescription?.toLowerCase().includes('zweryfikować'))

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src="/assets/logo.png" alt="LexTool" className="h-16 mx-auto mb-4" />
            <p className="text-gray-500 mt-2">LexTool — AI dla prawników</p>
          </div>

          {/* Trial registration success message */}
          {isTrialRegistration && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-800">
                    Konto utworzone pomyślnie!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Sprawdź skrzynkę email, aby zweryfikować adres, a następnie zaloguj się poniżej.
                    Masz 7-dniowy okres próbny.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tagline */}
          <p className="font-medium text-gray-900 text-center mb-6">
            Przyspiesz pracę na dokumentach, zachowując pełną kontrolę.
          </p>

          {/* Features */}
          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Szkice pism i dokumentów w kilka minut</p>
                <p className="text-sm text-gray-500">Szkice pism (pozwy, odpowiedzi na pozew, wezwania, wnioski) oraz umów, regulaminów i klauzul — jako punkt wyjścia do dalszej pracy.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="font-medium text-gray-900">Baza LexTool: 2,5 mln+ przepisów i orzeczeń z polskiego prawa</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Research i analiza w minutach</p>
                <p className="text-sm text-gray-500">Podsumowania, porównania wersji, identyfikacja kluczowych zapisów, ryzyk i braków.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Cytowania i ścieżka źródeł</p>
                <p className="text-sm text-gray-500">Widzisz, skąd pochodzi informacja.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Praca zespołowa, sprawy i wersje</p>
                <p className="text-sm text-gray-500">Projekty, komentarze, współdzielenie materiałów i historia zmian w jednym miejscu.</p>
              </div>
            </div>
          </div>

          {/* Email verification message */}
          {isEmailVerificationError && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-yellow-800">
                    Zweryfikuj swoj adres email
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Wyslalismy link weryfikacyjny na Twoj adres email.
                    Kliknij w link, aby aktywowac konto, a nastepnie sprobuj zalogowac sie ponownie.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Other errors */}
          {error && !isEmailVerificationError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">
                    Blad logowania
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {errorDescription || 'Wystapil blad podczas logowania. Sprobuj ponownie.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Login button */}
          <button
            onClick={() => loginWithRedirect({
              authorizationParams: {
                prompt: isEmailVerificationError ? 'login' : undefined,
              },
            })}
            className="w-full btn-primary py-3 text-base"
          >
            {isEmailVerificationError ? 'Sprobuj ponownie' : 'Zaloguj się'}
          </button>

          {/* Register link */}
          <div className="mt-4 text-center">
            <a
              href="https://www.lextool.pl/cennik"
              className="text-sm text-blue-600 hover:underline font-medium"
            >
              Nie masz konta? Zobacz plany i zarejestruj się
            </a>
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-6">
            {isEmailVerificationError
              ? 'Sprawdz folder spam, jesli nie widzisz emaila.'
              : 'Logowanie przez Auth0. Twoje dane są bezpieczne.'}
          </p>
        </div>

        {/* Copyright */}
        <p className="text-center text-xs text-gray-400 mt-6">
          © 2025 LexTool Sp. z o.o. Wszelkie prawa zastrzeżone. All rights reserved. | Korzystanie ze strony oraz narzędzia LexTool oznacza akceptację Regulaminu. | By using the LexTool website and tool, you agree to the Terms. | <a href="https://www.lextool.pl/regulamin" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200">Regulamin</a> | <a href="https://www.lextool.pl/polityka-prywatnosci" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-200">Polityka prywatności</a>
        </p>
      </div>
    </div>
  )
}
